// Shared helpers for the roadmap UI. The roadmap content itself now lives in
// the translation files (roadmapReleases / roadmapStatuses per locale).

export const normalize = (value) =>
  (value || '').toString().trim().toLowerCase();

// Canonical status keys used across the roadmap UI for icons, colours and
// filtering. Keep this list as the single source of truth.
export type StatusKey = 'achieved' | 'progress' | 'planned';

// Known aliases per canonical key, per locale. Add a locale's status wording
// here (fully normalized: trimmed + lowercased) to make it recognized — this
// is explicit and extensible, unlike ad-hoc substring guessing. The arrays are
// checked with both exact-equality and startsWith, so short stems (e.g.
// "réalis") also cover inflected forms ("réalisé", "réalisée").
export const STATUS_ALIASES: Record<StatusKey, string[]> = {
  achieved: ['achieved', 'complete', 'completed', 'done', 'réalis', 'realis', 'terminé', 'termine'],
  progress: ['in progress', 'in-progress', 'ongoing', 'en cours', 'en-cours'],
  planned: ['planned', 'upcoming', 'planifié', 'planifie', 'prévu', 'prevu', 'à venir', 'a venir']
};

// Map any status string (any supported locale) to its canonical key. Falls
// back to 'planned' when nothing matches so the UI still renders sensibly.
export const statusKey = (status): StatusKey => {
  const s = normalize(status);
  if (!s) return 'planned';
  const keys = Object.keys(STATUS_ALIASES) as StatusKey[];
  const hit = keys.find((key) =>
    STATUS_ALIASES[key].some((alias) => s === alias || s.startsWith(alias) || s.includes(alias))
  );
  return hit || 'planned';
};

// Match a release status to its CMS status metadata (image + label). Compares
// by canonical key, so a status derived in one language (e.g. English
// 'Achieved' from deriveQuarterStatus) still resolves against a status list
// provided in another (e.g. French 'Réalisé').
export const findStatusMeta = (statusList, status) =>
  statusList.find((s) => statusKey(s.status) === statusKey(status));

// Derive a card-level status from its items:
//   - all achieved            -> Achieved
//   - any in progress         -> In progress
//   - all planned             -> Planned
//   - mixed achieved/planned  -> In progress (work is partially done)
export const deriveQuarterStatus = (items) => {
  if (!items.length) return 'Planned';
  const keys = items.map((i) => statusKey(i.status));
  if (keys.every((k) => k === 'achieved')) return 'Achieved';
  if (keys.every((k) => k === 'planned')) return 'Planned';
  return 'In progress';
};

export const removePTags = (html: string): string => {
  if (!html) return '';
  // Use DOMParser to strip tags and decode entities (e.g. &nbsp; → space).
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').trim();
};

// Map a booking flow value (any supported locale) to a canonical key so that
// filtering works even when the selected filter value (English, e.g. "Prime
// booking") differs from the release data value (localized, e.g. French
// "Réservation principale").
export type BookingFlowKey = 'prime' | 'post';
export const bookingFlowKey = (value): BookingFlowKey => {
  const s = normalize(value);
  return s.includes('prime') || s.includes('principale') ? 'prime' : 'post';
};


// Format an ISO date string ("2026-01-02") as "January 2, 2026" (or the
// locale equivalent, e.g. "2 janvier 2026" for fr-CA).
export const formatLongDate = (date, locale = 'en-US') => {
  if (!date) return '';
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

// Prepend the Drupal content CDN base URL to relative image paths (e.g. /s3fs-public/...).
export const getDrupalImageUrl = (url: string): string => {
  if (!url || url.startsWith('http') || url.startsWith('data:')) return url;
  const env = process.env['REACT_APP_ENV_LOADED'];
  let baseUrl = 'https://content-ndc-portal.aircanada.com';
  if (env === 'DEV' || env === 'INT' || env === 'CRT') {
    baseUrl = 'https://content-ndc-portal-int.aircanada.com';
  } else if (env === 'BAT' || env === 'PREPROD') {
    baseUrl = 'https://content-ndc-portal-bat.aircanada.com';
  }
  return baseUrl + url;
};